import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft, Send, Copy, Check, Sparkles, Paperclip, Mic, Bot, Users,
  Loader2, RotateCcw, Square, Zap, FileText, MoreHorizontal, X, MessageSquare,
  Newspaper, Bell, Search, CalendarClock, Archive, Trash2, Download, FileDown,
  ChevronDown, ChevronRight, Terminal, Brain, Settings2, ToggleLeft, ToggleRight,
  LifeBuoy, ShieldCheck, AlertTriangle,
} from 'lucide-react';
import { haptic } from '../../../telegram';
import { listMessages, streamMessage, createConversation, deleteConversation, setArchived, uploadFile, transcribeVoice, setConversationAgent, getConversation, getStreamingPrefs, updateStreamingPrefs } from '../../../api/chatApi';
import type { StreamError, StreamingPrefs } from '../../../api/chatApi';
import type { Agent } from '../../../types/contracts';
import { agentsApi } from '../../../api/agentsApi';
import { feedbackApi } from '../../../api/feedbackApi';
import { Markdown } from '../../components/Markdown';
import SmartCreate from '../../components/SmartCreate';

declare const window: Window & { Telegram?: any };

interface Attach { fileId: string; name: string; sizeKb: number; isImage?: boolean; previewUrl?: string; }
interface Msg {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  ts: number;
  status?: string;
  automationPrompt?: string;
  files?: { name: string; sizeKb: number; isImage?: boolean; previewUrl?: string }[];
}
interface Usage { promptTokens: number; completionTokens: number; costUsd: number; }
interface Props { conversationId: string | null; onBack: (action?: { type: 'archived' | 'deleted'; id: string }) => void; headerTitle?: string; headerSubtitle?: string; initialText?: string; }

const GENERIC_ERROR = 'Не получилось отправить сообщение. Проверьте интернет и попробуйте ещё раз';

const STARTER_CHIPS: { icon: React.ReactNode; text: string }[] = [
  { icon: <Sparkles />, text: 'Собрать автоматизацию' },
  { icon: <Newspaper />, text: 'Утренний дайджест новостей' },
  { icon: <Bell />, text: 'Напоминания по расписанию' },
  { icon: <Search />, text: 'Следить за упоминаниями' },
  { icon: <CalendarClock />, text: 'Еженедельный отчёт' },
];
const POPULAR_TOPICS: { icon: React.ReactNode; title: string; prompt: string }[] = [
  { icon: <Sparkles />, title: 'Рекомендации по автоматизации', prompt: 'Посоветуй, что можно автоматизировать в моей работе' },
  { icon: <FileText />, title: 'Полезный набор для отчётов', prompt: 'Собери автоматизацию для регулярных отчётов' },
];

function isMobileTG(): boolean {
  const p = (window.Telegram?.WebApp?.platform || '').toLowerCase();
  return p === 'android' || p === 'ios';
}
function fmtTime(ts: number) { return new Date(ts).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }); }

// Ссылки на сгенерированные агентом файлы (/api/files/<uuid>) выносим из текста в карточки.
const FILE_LINK_RE = /(?:https?:\/\/[^\s)\]]+)?\/api\/files\/[0-9a-fA-F-]{36}[^\s)\]]*/g;
function extractFileLinks(text: string): { cleaned: string; urls: string[] } {
  const urls = Array.from(new Set(text.match(FILE_LINK_RE) || []));
  let cleaned = text;
  for (const u of urls) {
    const esc = u.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    cleaned = cleaned.replace(new RegExp('\\[[^\\]]*\\]\\(\\s*' + esc + '\\s*\\)', 'g'), '');
    cleaned = cleaned.split(u).join('');
  }
  cleaned = cleaned.replace(/\(\s*\)/g, '').replace(/\n{3,}/g, '\n\n').trim();
  return { cleaned, urls };
}
function absUrl(url: string): string {
  if (/^https?:/i.test(url)) return url;
  const base = ((window as any).__API_BASE__ || import.meta.env.VITE_API_BASE_URL || location.origin).replace(/\/+$/, '');
  return base + url;
}
function downloadFileUrl(url: string, name = 'file') {
  const abs = absUrl(url);
  const tg = window.Telegram?.WebApp;
  if (tg?.openLink) tg.openLink(abs);
  else if (tg?.downloadFile) tg.downloadFile({ url: abs, file_name: name });
  else window.open(abs, '_blank');
}
function FileCard({ url }: { url: string }) {
  return (
    <button className="rf-dlcard" onClick={() => { haptic('light'); downloadFileUrl(url); }}>
      <span className="rf-dlcard-ic"><FileDown /></span>
      <span className="rf-dlcard-body">
        <span className="rf-dlcard-name">Файл готов</span>
        <span className="rf-dlcard-sub">Нажмите, чтобы скачать</span>
      </span>
      <Download className="rf-dlcard-arrow" />
    </button>
  );
}
function AssistantBody({ content, convId }: { content: string; convId: string | null }) {
  const { cleaned, urls } = extractFileLinks(content);
  return (
    <>
      {cleaned && <Markdown text={cleaned} conversationId={convId} />}
      {urls.map((u) => <FileCard key={u} url={u} />)}
    </>
  );
}


// ─── Агентская лента ──────────────────────────────────────────
interface AgentStep {
  type: 'thinking' | 'tool_call' | 'tool_result' | 'file';
  step?: number;
  text?: string;       // message_delta chunks
  tool?: string;       // tool name
  args?: Record<string, unknown>;
  result?: string;
  ok?: boolean;
  truncated?: boolean;
  name?: string;       // file name
  url?: string;        // file url
  fileId?: string;
}

function ToolArgsView({ args }: { args: Record<string, unknown> }) {
  const str = JSON.stringify(args, null, 2);
  return (
    <pre className="rf-agent-code">{str.length > 500 ? str.slice(0, 500) + '…' : str}</pre>
  );
}

function AgentFeedItem({ step, defaultExpanded = false }: { step: AgentStep; defaultExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (step.type === 'file' && step.url) {
    return (
      <div className="rf-agent-file">
        <FileDown className="rf-agent-file-ic" />
        <span className="rf-agent-file-name">{step.name || 'Файл'}</span>
        <button className="rf-agent-file-btn" onClick={() => downloadFileUrl(step.url!, step.name)}>
          <Download size={14} /> Скачать
        </button>
      </div>
    );
  }

  if (step.type === 'thinking') {
    const text = (step.text || '').trim();
    if (text.startsWith('{"tool"') || text.startsWith('{\n  "tool"')) {
      return null;
    }
    return (
      <div className="rf-agent-thinking">
        <button className="rf-agent-row" style={{ background: 'transparent', border: 'none', padding: '2px 0', fontSize: '12px', color: 'var(--text-secondary, #888)', opacity: 0.8 }} onClick={() => setExpanded(!expanded)}>
          <span className="rf-agent-label">Рассуждение</span>
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
        {expanded && step.text && (
          <div className="rf-agent-body">
            <p className="rf-agent-text">{step.text}</p>
          </div>
        )}
      </div>
    );
  }

  if (step.type === 'tool_call' && step.tool === 'обращение_к_агенту') {
    const args = step.args || {};
    const agentName = args['имя'] || 'Субагент';
    const taskDesc = args['задача'] || '';
    return (
      <div className="rf-agent-tool orchestrator-delegation">
        <div className="rf-agent-row" style={{ background: 'transparent', border: 'none', padding: '2px 0', fontSize: '12px', color: 'var(--accent)', opacity: 0.9, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={() => setExpanded(!expanded)}>
          <span className="rf-agent-label">Делегирование: {String(agentName)}</span>
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </div>
        {expanded && (
          <div className="rf-agent-body">
            <p className="rf-agent-text"><strong>Задача:</strong> {String(taskDesc)}</p>
          </div>
        )}
      </div>
    );
  }

  if (step.type === 'tool_call') {
    const hasArgs = step.args && Object.keys(step.args).length > 0;
    return (
      <div className="rf-agent-tool">
        <div className="rf-agent-row" style={{ background: 'transparent', border: 'none', padding: '2px 0', fontSize: '12px', color: 'var(--text-secondary, #888)', opacity: 0.8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={() => setExpanded(!expanded)}>
          <span className="rf-agent-label">Вызов: {step.tool}</span>
          {hasArgs && (expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
        </div>
        {expanded && hasArgs && (
          <div className="rf-agent-body">
            <ToolArgsView args={step.args!} />
          </div>
        )}
      </div>
    );
  }

  if (step.type === 'tool_result') {
    const ok = step.ok !== false;
    return (
      <div className={"rf-agent-result" + (ok ? '' : ' err')}>
        <div className="rf-agent-row" style={{ background: 'transparent', border: 'none', padding: '2px 0', fontSize: '12px', color: 'var(--text-secondary, #888)', opacity: 0.8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={() => setExpanded(!expanded)}>
          <span className="rf-agent-label">{ok ? 'Результат' : 'Ошибка'}: {step.tool}{step.truncated ? ' (обрезан)' : ''}</span>
          {step.result && (expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
        </div>
        {expanded && step.result && (
          <div className="rf-agent-body">
            <pre className="rf-agent-code">{step.result}</pre>
          </div>
        )}
      </div>
    );
  }

  return null;
}

function AgentFeed({ steps }: { steps: AgentStep[] }) {
  if (steps.length === 0) return null;
  return (
    <div className="rf-agent-feed">
      {steps.map((s, i) => (
        <AgentFeedItem key={i} step={s} defaultExpanded={s.type === 'file'} />
      ))}
    </div>
  );
}

// Панель настроек стриминга
function StreamingPrefsPanel({ onClose }: { onClose: () => void }) {
  const [prefs, setPrefs] = useState<StreamingPrefs>({
    show_reasoning: true,
    show_tool_details: true,
    stream_intermediate: true,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStreamingPrefs().then(setPrefs).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = async (key: keyof StreamingPrefs) => {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    try { await updateStreamingPrefs({ [key]: updated[key] }); } catch {}
  };

  const ToggleRow = ({ label, k }: { label: string; k: keyof StreamingPrefs }) => (
    <div className="rf-pref-row">
      <span className="rf-pref-label">{label}</span>
      <button className="rf-pref-toggle" onClick={() => toggle(k)} aria-label={label}>
        {prefs[k] ? <ToggleRight size={22} className="on" /> : <ToggleLeft size={22} className="off" />}
      </button>
    </div>
  );

  return (
    <div className="rf-prefs-panel">
      <div className="rf-prefs-head">
        <Settings2 size={16} /> Настройки стриминга
        <button className="rf-prefs-close" onClick={onClose}><X size={16} /></button>
      </div>
      {loading ? (
        <div className="rf-loading"><div className="rf-spin" /></div>
      ) : (
        <>
          <ToggleRow label="Показывать ход мыслей" k="show_reasoning" />
          <ToggleRow label="Детали инструментов (аргументы + результат)" k="show_tool_details" />
          <ToggleRow label="Промежуточные ответы шагов" k="stream_intermediate" />
        </>
      )}
    </div>
  );
}

export function ChatView({ conversationId, onBack, headerTitle, headerSubtitle, initialText }: Props) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [convId, setConvId] = useState<string | null>(conversationId);
  const [copied, setCopied] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // Отправка диалога в поддержку: окно согласия → отправка → подтверждение.
  const [reportOpen, setReportOpen] = useState(false);
  const [reportComment, setReportComment] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportDone, setReportDone] = useState(false);
  const [reportErr, setReportErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<Attach[]>([]);   // приложенные, но ещё не отправленные
  const [recording, setRecording] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const [recSec, setRecSec] = useState(0);
  const [mediaRec, setMediaRec] = useState<MediaRecorder | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [retryText, setRetryText] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [smartOpen, setSmartOpen] = useState(false);
  const [smartPrompt, setSmartPrompt] = useState('');
  // Агентская лента
  const [agentSteps, setAgentSteps] = useState<AgentStep[]>([]);
  const agentStepsRef = useRef<AgentStep[]>([]);
  const [prefsOpen, setPrefsOpen] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const autoSentRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const voiceCancelRef = useRef(false);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { agentsApi.list().then((r) => setAgents(r || [])).catch(() => {}); }, []);

  useEffect(() => {
    if (!conversationId) return;
    setHistoryLoading(true);
    listMessages(conversationId).then((ms: any[]) => {
      setMsgs(ms.map((m: any) => ({ id: m.id || Math.random().toString(), role: m.role, content: m.content, ts: new Date(m.createdAt || Date.now()).getTime() })));
    }).catch(() => {}).finally(() => setHistoryLoading(false));
    // Запоминаем выбранного для беседы агента (иначе после переоткрытия сбрасывается на оркестратора).
    getConversation(conversationId).then((c: any) => { setSelectedAgent(c?.agentId ?? null); }).catch(() => {});
  }, [conversationId]);

  useEffect(() => () => {
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    if (recTimerRef.current) clearInterval(recTimerRef.current);
  }, []);

  const onScroll = () => {
    const el = scrollRef.current; if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  useEffect(() => { if (nearBottomRef.current) bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  const ensureConvId = useCallback(async (): Promise<string | null> => {
    if (convId) return convId;
    try {
      const conv = await createConversation();
      setConvId(conv.id);
      if (selectedAgent !== null) { try { await setConversationAgent(conv.id, selectedAgent); } catch {} }
      return conv.id;
    } catch { return null; }
  }, [convId, selectedAgent]);

  const selectAgent = useCallback(async (id: string | null) => {
    setSelectedAgent(id); setMenuOpen(false); haptic('light');
    if (convId) { try { await setConversationAgent(convId, id); } catch {} }
  }, [convId]);

  const send = useCallback(async (override?: string) => {
    const text = (override ?? input).trim();
    const attached = pendingFiles;
    if ((!text && attached.length === 0) || sending) return;
    haptic('light'); if (override === undefined) setInput(''); setSending(true);
    setRetryText(null);
    const userMsg: Msg = {
      id: Date.now().toString(), role: 'user', content: text, ts: Date.now(),
      files: attached.length ? attached.map((f) => ({ name: f.name, sizeKb: f.sizeKb, isImage: f.isImage, previewUrl: f.previewUrl })) : undefined,
    };
    setMsgs((prev) => [...prev, userMsg]);
    setPendingFiles([]);
    const assistantId = (Date.now() + 1).toString();
    // Сброс агентской ленты для нового запроса
    agentStepsRef.current = [];
    setAgentSteps([]);
    setMsgs((prev) => [...prev, { id: assistantId, role: 'assistant', content: '', status: 'Думаю…', ts: Date.now() }]);

    const showError = (message: string) => {
      setMsgs((prev) => prev.map((m) => m.id === assistantId ? { ...m, role: 'system' as any, content: message, status: undefined } : m));
      setRetryText(text);
    };

    let cid = convId;
    if (!cid) {
      try {
        const conv = await createConversation();
        cid = conv.id; setConvId(conv.id);
        if (selectedAgent !== null) { try { await setConversationAgent(conv.id, selectedAgent); } catch {} }
      } catch { showError(GENERIC_ERROR); setSending(false); return; }
    }

    const controller = new AbortController();
    abortRef.current = controller;
    let buffer = '';
    try {
      await streamMessage({
        conversationId: cid, text, fileIds: attached.map((f) => f.fileId),
        signal: controller.signal,
        onEvent: (ev) => {
          if (ev.type === 'status') setMsgs((prev) => prev.map((m) => m.id === assistantId && !m.content ? { ...m, status: ev.text } : m));
          if (ev.type === 'automation_suggestion') setMsgs((prev) => prev.map((m) => m.id === assistantId ? { ...m, automationPrompt: (ev as any).prompt } : m));
          if (ev.type === 'delta') { buffer += ev.text || ''; setMsgs((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: buffer, status: undefined } : m)); }
          if (ev.type === 'usage') setUsage({ promptTokens: ev.promptTokens || 0, completionTokens: ev.completionTokens || 0, costUsd: ev.costUsd || 0 });
          if (ev.type === 'done') setMsgs((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: buffer || m.content, status: undefined } : m));
          if (ev.type === 'error') showError(ev.message || GENERIC_ERROR);
          // Предупреждение о лимите токенов (~80%): показываем системным сообщением над ответом.
          if (ev.type === 'warning') {
            const warnText = (ev as any).text as string;
            if (warnText) setMsgs((prev) => {
              const warnMsg: Msg = { id: 'warn-' + Date.now(), role: 'system' as any, content: warnText, ts: Date.now() };
              const idx = prev.findIndex((m) => m.id === assistantId);
              return idx < 0 ? [...prev, warnMsg] : [...prev.slice(0, idx), warnMsg, ...prev.slice(idx)];
            });
          }
          // Агентская лента: промежуточные шаги
          if (ev.type === 'message_delta') {
            const step = ev.step as number;
            const steps = agentStepsRef.current;
            const existingIdx = steps.findIndex((s) => s.type === 'thinking' && s.step === step);
            if (existingIdx >= 0) {
              steps[existingIdx] = { ...steps[existingIdx], text: (steps[existingIdx].text || '') + (ev.text || '') };
            } else {
              steps.push({ type: 'thinking', step, text: ev.text || '' });
            }
            agentStepsRef.current = [...steps];
            setAgentSteps([...steps]);
          }
          if (ev.type === 'tool_call') {
            // Удаляем "thinking" (сырой JSON), если для этого шага вызван инструмент
            const steps = agentStepsRef.current.filter(s => !(s.type === 'thinking' && s.step === ev.step));
            steps.push({ type: 'tool_call' as const, step: ev.step as number, tool: ev.tool as string, args: ev.args as Record<string, unknown> | undefined });
            agentStepsRef.current = steps;
            setAgentSteps(steps);
          }
          if (ev.type === 'tool_result') {
            const steps = [...agentStepsRef.current, { type: 'tool_result' as const, step: ev.step as number, tool: ev.tool as string, ok: ev.ok as boolean, result: ev.result as string | undefined, truncated: ev.truncated as boolean | undefined }];
            agentStepsRef.current = steps;
            setAgentSteps(steps);
          }
          if (ev.type === 'file') {
            const steps = [...agentStepsRef.current, { type: 'file' as const, name: ev.name as string, url: ev.url as string, fileId: ev.fileId as string | undefined }];
            agentStepsRef.current = steps;
            setAgentSteps(steps);
          }
        },
        onError: (err: StreamError) => showError(err.status === 429 ? (err.message || GENERIC_ERROR) : GENERIC_ERROR),
      });
    } finally {
      setMsgs((prev) => prev.map((m) => m.id === assistantId && !m.content && !m.automationPrompt && m.role === 'assistant'
        ? { ...m, content: buffer || '⏹️ Остановлено', status: undefined } : m));
      abortRef.current = null; setSending(false);
    }
  }, [input, sending, convId, selectedAgent, pendingFiles]);

  const stop = useCallback(() => { haptic('medium'); abortRef.current?.abort(); }, []);

  useEffect(() => {
    if (initialText && !autoSentRef.current) {
      autoSentRef.current = true;
      const id = setTimeout(() => { send(initialText); }, 200);
      return () => clearTimeout(id);
    }
  }, [initialText, send]);

  const retry = useCallback(() => { const t = retryText; if (!t) return; setRetryText(null); haptic('light'); send(t); }, [retryText, send]);
  const copy = (text: string, id: string) => { navigator.clipboard.writeText(text).then(() => { setCopied(id); setTimeout(() => setCopied(null), 1400); }).catch(() => {}); };

  const openWizard = (prompt: string) => { haptic('medium'); setSmartPrompt(prompt); setSmartOpen(true); };
  const onWizardInstalled = (r: { kind: string; title?: string; lines?: { label: string; value: string }[]; tools?: string[] }) => {
    const parts: string[] = [`✅ **${r.title || (r.kind === 'agent' ? 'Агент' : 'Автоматизация')}** создан${r.kind === 'agent' ? '' : 'а'}.`];
    for (const l of r.lines ?? []) parts.push(`**${l.label}:** ${l.value}`);
    if (r.tools?.length) parts.push(`**Инструменты:** ${r.tools.join(', ')}`);
    parts.push('Открыть можно в разделе «Автоматизации» в нижнем меню.');
    setMsgs((prev) => [...prev, { id: (Date.now() + 2).toString(), role: 'assistant', content: parts.join('\n\n'), ts: Date.now() }]);
  };

  const del = async () => { if (!convId) return; haptic('heavy'); try { await deleteConversation(convId); onBack({ type: 'deleted', id: convId }); } catch {} };
  const archive = async () => { if (!convId) return; haptic('medium'); try { await setArchived(convId, true); onBack({ type: 'archived', id: convId }); } catch {} };
  // Открыть окно согласия на передачу переписки (из меню «три точки»).
  const openReport = () => {
    haptic('light');
    setMenuOpen(false);
    setReportErr(null);
    setReportDone(false);
    setReportComment('');
    setReportOpen(true);
  };
  const sendReport = async () => {
    if (!convId || reportBusy) return;
    setReportBusy(true);
    setReportErr(null);
    try {
      await feedbackApi.sendChatReport(convId, reportComment.trim() || undefined);
      haptic('medium');
      setReportDone(true);
    } catch (e) {
      setReportErr(e instanceof Error ? e.message : 'Не удалось отправить');
    } finally {
      setReportBusy(false);
    }
  };

  // Файл: аплоадим и добавляем в «приложенные», НЕ отправляем сразу.
  const onFileClick = async () => { const cid = await ensureConvId(); if (!cid) return; fileInputRef.current?.click(); };
  const onFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const cid = convId || (await ensureConvId());
    if (!cid) return;
    haptic('light'); setUploading(true);
    try {
      const r = await uploadFile(cid, file);
      const isImage = (file.type || '').startsWith('image/');
      const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
      setPendingFiles((prev) => [...prev, { fileId: r.fileId, name: r.name, sizeKb: Math.round(r.sizeBytes / 1024), isImage, previewUrl }]);
    } catch {} finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };
  const removePending = (fileId: string) => { haptic('light'); setPendingFiles((prev) => prev.filter((f) => f.fileId !== fileId)); };

  const getAudioStream = async () => {
    if (audioStreamRef.current?.active) return audioStreamRef.current;
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    audioStreamRef.current = stream;
    return stream;
  };
  const openVoice = async () => {
    const cid = await ensureConvId(); if (!cid) return;
    haptic('light'); setVoiceMode(true);
    try {
      const stream = await getAudioStream();
      const rec = new MediaRecorder(stream);
      audioChunksRef.current = []; voiceCancelRef.current = false;
      rec.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
        setRecSec(0);
        if (voiceCancelRef.current) return;
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (cid && blob.size > 0) {
          setUploading(true);
          try {
            const r = await transcribeVoice(cid, blob);
            if (r.text) setInput((prev) => prev + (prev ? ' ' : '') + r.text);
          } catch (e) {
            // Ошибку расшифровки больше не глушим: при лимите (429) пользователь
            // должен видеть причину, а не пустое поле ввода.
            const raw = e instanceof Error ? e.message : String(e);
            let text = 'Не удалось распознать голосовое сообщение. Попробуйте ещё раз или напишите текстом.';
            const jsonPart = raw.match(/\{[\s\S]*\}/);
            if (jsonPart) {
              try {
                const parsed = JSON.parse(jsonPart[0]) as { error?: string };
                if (parsed?.error) text = String(parsed.error);
              } catch { /* оставляем общее сообщение */ }
            }
            const errMsg: Msg = { id: 'voice-err-' + Date.now(), role: 'system' as any, content: text, ts: Date.now() };
            setMsgs((prev) => [...prev, errMsg]);
          } finally { setUploading(false); }
        }
      };
      rec.start(); setMediaRec(rec); setRecording(true); haptic('medium');
      setRecSec(0); recTimerRef.current = setInterval(() => setRecSec((s) => s + 1), 1000);
    } catch { setRecording(false); setVoiceMode(false); }
  };
  const finishVoice = () => { voiceCancelRef.current = false; if (mediaRec?.state === 'recording') mediaRec.stop(); setRecording(false); setMediaRec(null); setVoiceMode(false); haptic('light'); };
  const cancelVoice = () => { voiceCancelRef.current = true; if (mediaRec?.state === 'recording') mediaRec.stop(); setRecording(false); setMediaRec(null); setVoiceMode(false); haptic('light'); };

  const activeAgentName = selectedAgent === null ? 'Оркестратор' : (agents.find((a) => a.id === selectedAgent)?.name || 'Агент');
  const title = headerTitle || activeAgentName;
  const showStarter = msgs.length === 0 && !headerSubtitle && !historyLoading;
  const canSend = input.trim().length > 0 || pendingFiles.length > 0;

  return (
    <div className="rf-chat">
      <div className="rf-cbar">
        <button className="rf-cbar-btn" onClick={() => { haptic('light'); onBack(); }} aria-label="Назад"><ArrowLeft /></button>
        <div className="rf-cbar-title">{title}{sending && <span className="rf-cbar-sub">печатает…</span>}</div>
        <button className="rf-cbar-btn" onClick={() => { haptic('light'); setMenuOpen((v) => !v); }} aria-label="Меню"><MoreHorizontal /></button>
      </div>

      {menuOpen && (
        <div className="rf-cmenu">
          <div className="rf-cmenu-label">Агент</div>
          <div className="rf-cmenu-scroll">
            <button className={'rf-cmenu-row' + (selectedAgent === null ? ' on' : '')} onClick={() => selectAgent(null)}>
              <span className="rf-ava sm"><Sparkles /></span>Оркестратор
              {selectedAgent === null && <Check className="rf-cmenu-check" />}
            </button>
            {agents.map((a) => (
              <button key={a.id} className={'rf-cmenu-row' + (selectedAgent === a.id ? ' on' : '')} onClick={() => selectAgent(a.id)}>
                <span className="rf-ava sm"><Bot /></span>{a.name}
                {selectedAgent === a.id && <Check className="rf-cmenu-check" />}
              </button>
            ))}
          </div>
          {convId && (
            <>
              <button className="rf-cmenu-row rf-cmenu-support" onClick={openReport}>
                <span className="rf-ava sm"><LifeBuoy /></span>Отправить в поддержку
              </button>
              <div className="rf-cmenu-foot">
                <button className="rf-cmenu-act" onClick={archive}><Archive />В архив</button>
                <button className="rf-cmenu-act danger" onClick={del}><Trash2 />Удалить</button>
              </div>
            </>
          )}
        </div>
      )}

      {reportOpen && (
        <div className="rf-sheet-backdrop" onClick={() => { if (!reportBusy) setReportOpen(false); }}>
          <div className="rf-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="rf-sheet-handle" />
            <div className="rf-sheet-head">
              <div className="rf-sheet-title">{reportDone ? 'Отправлено' : 'Отправить в поддержку'}</div>
              <button className="rf-btn ghost icon" onClick={() => setReportOpen(false)} disabled={reportBusy}><X /></button>
            </div>

            {reportDone ? (
              <div className="rf-sheet-body">
                <div className="rf-confirm-warn rf-report-ok"><ShieldCheck /><div>
                  Спасибо! Переписка ушла в поддержку. Мы разберём ошибку и при необходимости ответим вам в боте.
                </div></div>
              </div>
            ) : (
              <div className="rf-sheet-body">
                <div className="rf-confirm-warn"><AlertTriangle /><div>
                  Если вы отправите этот чат, команда поддержки получит доступ к <b>полной переписке</b>:
                  всем вашим сообщениям, ответам ассистента и техническим данным запуска.
                  Мы будем читать эту переписку, чтобы найти причину ошибки. Нажимая «Согласен и отправить», вы даёте на это согласие.
                </div></div>
                <textarea
                  className="rf-input rf-report-note"
                  placeholder="Что пошло не так? Необязательно"
                  value={reportComment}
                  onChange={(e) => setReportComment(e.target.value)}
                  rows={3}
                  maxLength={500}
                  disabled={reportBusy}
                />
                {reportErr && <div className="rf-report-err">{reportErr}</div>}
              </div>
            )}

            <div className="rf-sheet-actions">
              {reportDone ? (
                <button className="rf-btn primary" onClick={() => setReportOpen(false)}>Готово</button>
              ) : (
                <>
                  <button className="rf-btn primary" onClick={sendReport} disabled={reportBusy}>
                    {reportBusy ? <Loader2 className="spin" /> : <LifeBuoy />}Согласен и отправить
                  </button>
                  <button className="rf-btn" onClick={() => setReportOpen(false)} disabled={reportBusy}>Отмена</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={'rf-cscroll' + (showStarter ? ' rf-cscroll-home' : '')} ref={scrollRef} onScroll={onScroll}>
        {headerSubtitle && (<div className="rf-info rf-chat-intro"><Sparkles /><div>{headerSubtitle}</div></div>)}
        {historyLoading && (<div className="rf-loading"><div className="rf-spin" /><p>Загрузка истории…</p></div>)}

        {showStarter && (
          <div className="rf-home">
            <div className="rf-home-hero">
              <h1 className="rf-home-title">Привет!<br />Чем помочь?</h1>
              <p className="rf-home-sub">Ваш умный ассистент готов</p>
            </div>
            <div className="rf-home-chips">
              {STARTER_CHIPS.map((c) => (
                <button key={c.text} className="rf-hchip" onClick={() => { haptic('light'); send(c.text); }}>{c.icon}{c.text}</button>
              ))}
            </div>
            <div className="rf-home-topics">
              <div className="rf-home-topics-head"><span>Популярные темы</span></div>
              <div className="rf-home-topics-row">
                {POPULAR_TOPICS.map((t) => (
                  <button key={t.title} className="rf-topic" onClick={() => { haptic('light'); setInput(t.prompt); }}>
                    <span className="rf-topic-ic">{t.icon}</span>
                    <span className="rf-topic-title">{t.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {msgs.map((m, idx) => (
          <div key={m.id} className={'rf-msg ' + m.role}>
            {m.role === 'assistant' && <span className="rf-ava"><Sparkles /></span>}
            <div className="rf-msg-col">
              {m.role === 'assistant' && idx === msgs.length - 1 && agentSteps.length > 0 && (
                <AgentFeed steps={agentSteps} />
              )}
              <div className={'rf-bub ' + m.role}>
                {m.files && m.files.length > 0 && (
                  <div className="rf-bub-files">
                    {m.files.map((f, i) => (
                      f.isImage && f.previewUrl
                        ? <img key={i} className="rf-bub-img" src={f.previewUrl} alt={f.name} />
                        : <div key={i} className="rf-filechip"><FileText /><span>{f.name}</span><em>{f.sizeKb} КБ</em></div>
                    ))}
                  </div>
                )}
                {m.role === 'assistant' && !m.content && m.status ? (
                  <div className="rf-thinking"><span className="rf-thinking-dot" /><span className="rf-thinking-dot" /><span className="rf-thinking-dot" />{m.status}</div>
                ) : m.role === 'assistant' ? (
                  m.content && <AssistantBody content={m.content} convId={convId} />
                ) : (
                  m.content && <span>{m.content}</span>
                )}
              </div>

              {m.automationPrompt && (
                <button className="rf-auto-card" onClick={() => openWizard(m.automationPrompt!)}>
                  <span className="rf-auto-card-ic"><Zap /></span>
                  <span className="rf-auto-card-body">
                    <span className="rf-auto-card-title">Собрать автоматизацию</span>
                    <span className="rf-auto-card-sub">Задам пару уточнений и покажу план перед созданием</span>
                  </span>
                </button>
              )}

              {/* Маленькая иконка копирования у времени — и у ответов, и у своих сообщений */}
              <div className="rf-bubble-meta">
                <span>{fmtTime(m.ts)}</span>
                {(m.role === 'assistant' || m.role === 'user') && m.content && !m.status && (
                  <button className="rf-copy-ic" onClick={() => copy(m.content, m.id)} title="Копировать">
                    {copied === m.id ? <Check /> : <Copy />}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {retryText && !sending && (<div className="rf-retry"><button className="rf-retry-btn" onClick={retry}><RotateCcw />Повторить</button></div>)}
        <div ref={bottomRef} />
      </div>

      {usage && (<div className="rf-usage">{usage.promptTokens + usage.completionTokens} токенов · ${usage.costUsd.toFixed(4)}</div>)}

      {/* Настройки стриминга */}
      {prefsOpen && (
        <StreamingPrefsPanel
          onClose={() => setPrefsOpen(false)}
        />
      )}

      {/* Приложенные файлы — превью над полем ввода */}
      {pendingFiles.length > 0 && (
        <div className="rf-pending">
          {pendingFiles.map((f) => (
            <div key={f.fileId} className={'rf-pending-item' + (f.isImage ? ' img' : '')}>
              {f.isImage && f.previewUrl ? <img className="rf-pending-thumb" src={f.previewUrl} alt={f.name} /> : <FileText />}
              {!f.isImage && <span className="rf-pending-name">{f.name}</span>}
              <button className="rf-pending-x" onClick={() => removePending(f.fileId)} aria-label="Убрать"><X /></button>
            </div>
          ))}
        </div>
      )}

      {/* Единая панель ввода */}
      <div className="rf-inbar">
        <input ref={fileInputRef} type="file" className="rf-file-hidden" onChange={onFileSelect} />
        <button className="rf-inbar-ic" onClick={onFileClick} disabled={uploading} aria-label="Прикрепить">
          {uploading ? <Loader2 className="spin" /> : <Paperclip />}
        </button>
        <textarea className="rf-inbar-text" placeholder="Напишите сообщение…" rows={1} value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !isMobileTG()) { e.preventDefault(); send(); } }} />
        {!canSend && !sending && (
          <button className="rf-inbar-ic" onClick={openVoice} disabled={uploading} aria-label="Голос"><Mic /></button>
        )}
        {!canSend && !sending && (
          <button className="rf-inbar-ic" onClick={() => setPrefsOpen(!prefsOpen)} aria-label="Настройки стриминга"><Settings2 /></button>
        )}
        {sending ? (
          <button className="rf-inbar-send stop" onClick={stop} aria-label="Остановить"><Square /></button>
        ) : canSend ? (
          <button className="rf-inbar-send" onClick={() => send()} aria-label="Отправить"><Send /></button>
        ) : null}
      </div>

      {voiceMode && (
        <div className="rf-voice">
          <div className="rf-voice-title">{recording ? 'Слушаю…' : 'Обрабатываю…'}</div>
          <div className={'rf-orb' + (recording ? ' live' : '')}><div className="rf-orb-core" /></div>
          <div className="rf-voice-hint">{recording ? `Говорите — ${recSec > 0 ? `${recSec} сек` : 'запись идёт'}…` : 'Расшифровываю…'}</div>
          <div className="rf-voice-ctrls">
            <button className="rf-voice-side" onClick={cancelVoice} aria-label="Отмена"><X /></button>
            <button className="rf-mic-big" onClick={finishVoice} aria-label="Готово"><Check /></button>
            <button className="rf-voice-side" onClick={finishVoice} aria-label="В чат"><MessageSquare /></button>
          </div>
        </div>
      )}

      {/* Визард рендерим порталом в body — иначе тонет в stacking-контексте .rf-chat */}
      {createPortal(
        // .tmx нужен, чтобы резолвились токены дизайн-системы (--x-surface и т.п.),
        // иначе панель Sheet становится прозрачной вне корня .tmx.
        <div className="tmx" style={{ display: 'contents' }}>
          <SmartCreate open={smartOpen} initialPrompt={smartPrompt} onClose={() => setSmartOpen(false)} onInstalled={onWizardInstalled} />
        </div>,
        document.body,
      )}
    </div>
  );
}
